import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import ProductCard from "@/components/ProductCard";
import { Instagram, ArrowLeft } from "lucide-react";

export default function AtletaDetail() {
  const { fid } = useParams();
  const navigate = useNavigate();
  const { t, tr, L } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/fighters/${fid}`).then((r) => { setData(r.data); }).catch(() => navigate("/atletas"));
  }, [fid, navigate]);

  if (!data) return <div className="pt-32 pb-40 text-center text-white/40 font-display uppercase tracking-widest">…</div>;
  const { fighter: f, products } = data;
  const achievements = L(f, "achievements") || [];

  return (
    <div data-testid="athlete-detail-page" className="pt-16 lg:pt-20">
      <Seo
        title={`${f.name}${f.nickname ? ` "${f.nickname}"` : ""} | ${tr("Atletas VORNA", "VORNA Athletes")}`}
        description={`${f.name}${f.nickname ? ` "${f.nickname}"` : ""} — ${tr("atleta patrocinado por VORNA, la marca ecuatoriana de fightwear.", "VORNA sponsored athlete, the Ecuadorian fightwear brand.")}`}
        path={`/atletas/${f.id}`}
        image={f.image}
      />
      <section className="relative h-[75vh] overflow-hidden flex items-end">
        <img src={f.action_images?.[0] || f.image} alt={f.name} className="absolute inset-0 h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative mx-auto max-w-[1600px] w-full px-5 lg:px-10 pb-14">
          <Link to="/atletas" className="inline-flex items-center gap-2 text-xs tracking-widest text-white/60 hover:text-white mb-6"><ArrowLeft className="h-4 w-4" /> {t("back_athletes").toUpperCase()}</Link>
          <p className="font-heading uppercase text-vorna-accent tracking-wide text-xl mb-2">"{f.nickname}"</p>
          <h1 className="font-display uppercase tracking-tighter text-[14vw] lg:text-[9vw] leading-[0.8]">{f.name}</h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1600px] px-5 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-[1fr_360px] gap-12 lg:gap-20">
          <div>
            <Reveal>
              <h2 className="font-display uppercase tracking-tighter text-3xl lg:text-4xl mb-6">{t("bio")}</h2>
              <p className="text-lg text-white/60 leading-relaxed max-w-2xl">{L(f, "bio")}</p>
            </Reveal>
            {achievements.length > 0 && (
              <Reveal>
                <h3 className="font-display uppercase tracking-tighter text-2xl mt-12 mb-5">{t("achievements")}</h3>
                <ul className="space-y-3 max-w-xl">
                  {achievements.map((a, i) => <li key={i} className="flex gap-3 text-white/70"><span className="text-vorna-accent">✦</span>{a}</li>)}
                </ul>
              </Reveal>
            )}
            {f.action_images?.length > 1 && (
              <div className="mt-12 grid grid-cols-2 gap-4">
                {f.action_images.map((img, i) => <div key={i} className="aspect-[4/5] overflow-hidden bg-vorna-elevated"><img src={img} alt="" className="h-full w-full object-cover" /></div>)}
              </div>
            )}
          </div>
          <aside className="lg:sticky lg:top-28 lg:self-start space-y-5 border border-white/10 p-8">
            <Row label={t("record")} value={f.record} />
            <Row label={t("weight_class")} value={f.weight_class} />
            <Row label={t("disciplineL")} value={f.discipline} />
            <Row label={t("team")} value={f.gym} />
            <Row label={t("city")} value={[f.city, f.country].filter(Boolean).join(", ")} />
            {f.instagram && <a href={f.instagram} target="_blank" rel="noreferrer" data-testid="athlete-instagram" className="inline-flex items-center gap-2 text-vorna-accent hover:gap-3 transition-all"><Instagram className="h-5 w-5" /> {t("follow")}</a>}
          </aside>
        </div>

        {products?.length > 0 && (
          <div className="mt-24">
            <Reveal><h2 className="font-display uppercase tracking-tighter text-3xl lg:text-5xl mb-10">{t("gear_they_use")}</h2></Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">{products.map((p) => <ProductCard key={p.id} product={p} />)}</div>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="border-b border-white/10 pb-4">
      <p className="text-[10px] tracking-widest text-white/30 uppercase">{label}</p>
      <p className="font-semibold mt-1">{value}</p>
    </div>
  );
}
